/* eslint-disable no-param-reassign */
/* eslint-disable import/no-unresolved */
import { Fancybox } from '@fancyapps/ui';

const PAGE_SIZES = {
  default: 6,
  sm: 8,
  lg: 12,
};

const MEDIA_QUERIES = {
  sm: window.matchMedia('(min-width: 481px)'),
  lg: window.matchMedia('(min-width: 1024px)'),
};

function getPageSize() {
  if (MEDIA_QUERIES.lg.matches) {
    return PAGE_SIZES.lg;
  }

  if (MEDIA_QUERIES.sm.matches) {
    return PAGE_SIZES.sm;
  }

  return PAGE_SIZES.default;
}

function getColumnCount() {
  if (MEDIA_QUERIES.lg.matches) {
    return 3;
  }

  if (MEDIA_QUERIES.sm.matches) {
    return 2;
  }

  return 1;
}

function getVisibleItems(items, activeFilter) {
  if (activeFilter === 'all') {
    return items;
  }

  return items.filter((item) => item.dataset.gallery === activeFilter);
}

function setActiveFilterButton(buttons, activeButton) {
  buttons.forEach((button) => {
    const isActive = button === activeButton;
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });
}

function updateFilterScrollHint(navEl, filterEl) {
  const maxScroll = navEl.scrollWidth - navEl.clientWidth;
  const hasOverflow = maxScroll > 1;
  const { scrollLeft } = navEl;

  filterEl.classList.toggle('is-scrollable-start', hasOverflow && scrollLeft > 1);
  filterEl.classList.toggle('is-scrollable-end', hasOverflow && scrollLeft < maxScroll - 1);
}

function initFilterScrollHint(rootEl) {
  const filterEl = rootEl.querySelector('.gallery__filter');
  const navEl = rootEl.querySelector('[data-gallery-filter-nav]');

  if (!filterEl || !navEl) {
    return;
  }

  const update = () => {
    updateFilterScrollHint(navEl, filterEl);
  };

  navEl.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  MEDIA_QUERIES.lg.addEventListener('change', update);

  update();
}

function getColumnOffsetClass(index, columnCount) {
  if (columnCount === 1) {
    return '';
  }

  if (columnCount === 2) {
    return index === 1 ? 'gallery__column--offset-lg' : 'gallery__column--offset-sm';
  }

  if (index === 1) {
    return 'gallery__column--offset-lg';
  }

  return 'gallery__column--offset-sm';
}

function ensureColumns(gridEl, columnCount) {
  const existingColumns = Array.from(gridEl.querySelectorAll('[data-gallery-column]'));

  if (existingColumns.length === columnCount) {
    return existingColumns;
  }

  gridEl.innerHTML = '';

  const columns = [];

  for (let i = 0; i < columnCount; i += 1) {
    const column = document.createElement('div');
    column.className = `gallery__column ${getColumnOffsetClass(i, columnCount)}`.trim();
    column.setAttribute('data-gallery-column', '');
    gridEl.appendChild(column);
    columns.push(column);
  }

  return columns;
}

function getOrderedGalleryLinks(items, activeFilter) {
  return getVisibleItems(items, activeFilter)
    .map((item) => item.querySelector('.gallery__link'))
    .filter(Boolean);
}

function applyTilt(item, index) {
  item.classList.remove('gallery__item--tilt-right', 'gallery__item--tilt-left');
  item.classList.add(index % 2 === 0 ? 'gallery__item--tilt-right' : 'gallery__item--tilt-left');
}

function distributeItems(items, gridEl, stashEl, activeFilter, visibleCount) {
  const matchingItems = getVisibleItems(items, activeFilter);
  const matchingSet = new Set(matchingItems);
  const columnCount = getColumnCount();
  const columns = ensureColumns(gridEl, columnCount);

  columns.forEach((column) => {
    column.innerHTML = '';
  });

  matchingItems.forEach((item, index) => {
    const isVisible = index < visibleCount;

    if (isVisible) {
      applyTilt(item, index);
      columns[index % columnCount].appendChild(item);
    } else {
      stashEl.appendChild(item);
    }
  });

  items.forEach((item) => {
    if (!matchingSet.has(item)) {
      stashEl.appendChild(item);
    }
  });

  return matchingItems.length > visibleCount;
}

function bindGalleryLightbox(rootEl, items, getActiveFilter) {
  rootEl.addEventListener('click', (event) => {
    const link = event.target.closest('.gallery__link');

    if (!link || !rootEl.contains(link)) {
      return;
    }

    const item = link.closest('[data-gallery-item]');

    if (!item) {
      return;
    }

    const activeFilter = getActiveFilter();
    const inGroup = activeFilter === 'all' || item.dataset.gallery === activeFilter;

    if (!inGroup) {
      return;
    }

    const orderedLinks = getOrderedGalleryLinks(items, activeFilter);
    const startIndex = orderedLinks.indexOf(link);

    if (startIndex === -1) {
      return;
    }

    event.preventDefault();

    Fancybox.fromNodes(orderedLinks, {
      startIndex,
      triggerEl: link,
      triggerEvent: event,
    });
  });
}

function initGallery(rootEl) {
  const filterButtons = Array.from(rootEl.querySelectorAll('[data-gallery-filter-btn]'));
  const gridEl = rootEl.querySelector('[data-gallery-grid]');
  const stashEl = rootEl.querySelector('[data-gallery-stash]');
  const moreContainer = rootEl.querySelector('[data-gallery-more]');
  const moreBtn = rootEl.querySelector('[data-gallery-more-btn]');

  initFilterScrollHint(rootEl);

  if (!gridEl || !stashEl) {
    return;
  }

  const items = Array.from(stashEl.querySelectorAll('[data-gallery-item]'));

  if (!items.length) {
    return;
  }

  let activeFilter = 'all';
  let visibleCount = getPageSize();

  const updateView = () => {
    const hasMore = distributeItems(items, gridEl, stashEl, activeFilter, visibleCount);

    if (moreContainer) {
      moreContainer.hidden = !hasMore;
    }

    rootEl.classList.add('gallery--ready');
  };

  filterButtons.forEach((button) => {
    button.addEventListener('click', () => {
      activeFilter = button.dataset.filter || 'all';
      visibleCount = getPageSize();
      setActiveFilterButton(filterButtons, button);
      updateView();
    });
  });

  if (moreBtn) {
    moreBtn.addEventListener('click', () => {
      visibleCount += getPageSize();
      updateView();
    });
  }

  const handleBreakpointChange = () => {
    visibleCount = getPageSize();
    updateView();
  };

  MEDIA_QUERIES.sm.addEventListener('change', handleBreakpointChange);
  MEDIA_QUERIES.lg.addEventListener('change', handleBreakpointChange);

  bindGalleryLightbox(rootEl, items, () => activeFilter);
  updateView();
}

function initAll() {
  document.querySelectorAll('[data-gallery-block]').forEach(initGallery);
}

document.addEventListener('DOMContentLoaded', () => {
  initAll();
});
